import React, { useState } from 'react';
import { migrateJSONToSupabase } from '../../scripts/migrateToSupabase';
import { fetchHotelData } from '../../utils/dataManager';
import './DatabaseMigration.css';

/**
 * Database Migration Component
 * This component helps migrate data from JSON to Supabase
 */
const DatabaseMigration = () => {
  const [migrationStatus, setMigrationStatus] = useState('ready');
  const [migrationLog, setMigrationLog] = useState([]);
  const [error, setError] = useState(null);

  const addToLog = (message) => {
    setMigrationLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const runMigration = async () => {
    setMigrationStatus('running');
    setError(null);
    setMigrationLog([]);
    
    try {
      addToLog('Starting database migration...');
      
      // Fetch current JSON data
      addToLog('Fetching current JSON data...');
      const result = await fetchHotelData();
      
      if (!result.success) {
        throw new Error(`Failed to fetch JSON data: ${result.error}`);
      }

      addToLog('JSON data loaded successfully');
      addToLog(`Found ${Object.keys(result.data.menuItems || {}).length} menu sections`);
      addToLog(`Found ${result.data.events?.length || 0} events`);

      // Run the migration
      addToLog('Starting Supabase migration...');
      const migrationResult = await migrateJSONToSupabase(result.data);

      if (migrationResult.success) {
        addToLog('✅ Migration completed successfully!');
        setMigrationStatus('completed');
      } else {
        throw new Error(migrationResult.error);
      }

    } catch (err) {
      console.error('Migration failed:', err);
      setError(err.message);
      addToLog(`❌ Migration failed: ${err.message}`);
      setMigrationStatus('error');
    }
  };

  const resetMigration = () => {
    setMigrationStatus('ready');
    setMigrationLog([]);
    setError(null);
  };

  return (
    <div className="database-migration">
      <div className="migration-header">
        <h2>🗄️ Database Migration</h2>
        <p>Migrate your hotel data from JSON files to Supabase database</p>
      </div>

      <div className="migration-status">
        <div className={`status-indicator ${migrationStatus}`}>
          {migrationStatus === 'ready' && '⏳ Ready to migrate'}
          {migrationStatus === 'running' && '🔄 Migration in progress...'}
          {migrationStatus === 'completed' && '✅ Migration completed'}
          {migrationStatus === 'error' && '❌ Migration failed'}
        </div>
      </div>

      <div className="migration-actions">
        {migrationStatus === 'ready' && (
          <button 
            onClick={runMigration}
            className="btn btn-primary"
          >
            Start Migration
          </button>
        )}
        
        {(migrationStatus === 'completed' || migrationStatus === 'error') && (
          <button 
            onClick={resetMigration}
            className="btn btn-secondary"
          >
            Reset
          </button>
        )}
      </div>

      {error && (
        <div className="migration-error">
          <h3>Migration Error</h3>
          <p>{error}</p>
          {error.includes('row-level security policy') && (
            <div className="error-solution">
              <h4>Solution:</h4>
              <p>Run this SQL in your Supabase SQL Editor to fix the policy:</p>
              <pre>{`-- Fix RLS policies
DROP POLICY IF EXISTS "Allow authenticated write access" ON hotel_settings;
DROP POLICY IF EXISTS "Allow authenticated write access" ON menu_sections;
DROP POLICY IF EXISTS "Allow authenticated write access" ON menu_items;
DROP POLICY IF EXISTS "Allow authenticated write access" ON opening_hours;
DROP POLICY IF EXISTS "Allow authenticated write access" ON events;
DROP POLICY IF EXISTS "Allow authenticated write access" ON contact_info;

CREATE POLICY "Allow all write access" ON hotel_settings FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON menu_sections FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON menu_items FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON opening_hours FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON events FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON contact_info FOR ALL USING (true);`}</pre>
            </div>
          )}
        </div>
      )}

      {migrationLog.length > 0 && (
        <div className="migration-log">
          <h3>Migration Log</h3>
          <div className="log-entries">
            {migrationLog.map((entry, index) => (
              <div key={index} className="log-entry">
                {entry}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="migration-instructions">
        <h3>Setup Instructions</h3>
        <ol>
          <li>Create a Supabase project at <a href="https://supabase.com" target="_blank" rel="noopener noreferrer">supabase.com</a></li>
          <li>Go to Settings → API and copy your project URL and anon key</li>
          <li>Create a <code>.env</code> file in your project root with:
            <pre>{`REACT_APP_SUPABASE_URL=your_supabase_url
REACT_APP_SUPABASE_ANON_KEY=your_anon_key`}</pre>
          </li>
          <li>In your Supabase project, go to SQL Editor and run the database schema</li>
          <li>Click "Start Migration" above to transfer your data</li>
        </ol>
      </div>
    </div>
  );
};

export default DatabaseMigration;